import React from 'react';
import dayjs, { type Dayjs } from 'dayjs';

import type { DateType, MonthType } from './types';

type UseCalendarMatrix = (args: {
  initialDate: DateType;
  virtualizationMonthsOffset?: number;
}) => {
  matrix: MonthType[];
  go: (date: DateType) => void;
  goPrev: () => void;
  goNext: () => void;
  goYear: (year: string) => void;
  goMonth: (month: string) => void;
  currentDate: DateType;
};

const localeFirstDay = dayjs.localeData().firstDayOfWeek();

const alignToWeekStart = (date: Dayjs) => {
  const day = date.day();
  const diff = (day - localeFirstDay + 7) % 7;
  return date.subtract(diff, 'day');
};

const alignToWeekEnd = (date: Dayjs) => {
  const day = date.day();
  const diff = (localeFirstDay + 6 - day + 7) % 7;
  return date.add(diff, 'day');
};

export const useCalendarMatrix: UseCalendarMatrix = ({
  initialDate,
  virtualizationMonthsOffset = 0,
}) => {
  const [currentDate, setCurrentDate] = React.useState<DateType>(initialDate);

  const matrix = React.useMemo(() => {
    const result: MonthType[] = [];

    const { date: centerDate } = currentDate;

    const startMonth = centerDate.subtract(virtualizationMonthsOffset, 'month');
    const endMonth = centerDate.add(virtualizationMonthsOffset, 'month');

    const totalMonths = endMonth.diff(startMonth, 'month') + 1;

    for (let i = 0; i < totalMonths; i++) {
      const monthDate = startMonth.add(i, 'month');
      const startOfMonth = monthDate.startOf('month');
      const endOfMonth = monthDate.endOf('month');

      const calendarStart = alignToWeekStart(startOfMonth);
      const calendarEnd = alignToWeekEnd(endOfMonth);

      const days: DateType[] = [];
      let cursor = calendarStart;

      while (cursor.isBefore(calendarEnd) || cursor.isSame(calendarEnd, 'day')) {
        days.push({
          date: cursor,
          meta: {
            key: cursor.format('YYYY-MM-DD'),
            isCurrentMonth: cursor.month() === monthDate.month(),
            isToday: cursor.isSame(dayjs(), 'day'),
            isWeekend: [0, 6].includes(cursor.day()),
          },
        });

        cursor = cursor.add(1, 'day');
      }

      result.push({
        meta: {
          key: monthDate.format('YYYY-MM'),
          isCurrentMonth: monthDate.isSame(centerDate, 'month'),
          name: monthDate.format('MMMM YYYY'),
        },
        days,
      });
    }

    return result;
  }, [currentDate.date.valueOf(), virtualizationMonthsOffset]);

  const go = (date: DateType) => {
    setCurrentDate(date);
  };

  const goPrev = () => {
    setCurrentDate((c) => {
      return { date: c.date.subtract(1, 'month'), meta: {} };
    });
  };

  const goNext = () => {
    setCurrentDate((c) => {
      return { date: c.date.add(1, 'month'), meta: {} };
    });
  };

  const goYear = (year: string) => {
    setCurrentDate({ date: dayjs(`${year}-01-01`), meta: {} });
  };

  const goMonth = (month: string) => {
    const _month = String(month).padStart(2, '0');
    setCurrentDate((c) => {
      return { date: dayjs(`${c.date.format('YYYY')}-${_month}-01`), meta: {} };
    });
  };

  return { matrix, go, goPrev, goNext, goYear, goMonth, currentDate };
};
